/**
 * One quiet line of figures for exactly what is on screen: the median, the
 * slowest answer, how long the site was down, how many checks ran and how
 * many failed. It follows every zoom and scroll, so "how was it yesterday
 * evening" is answered by scrolling there rather than by hovering forty
 * points.
 *
 * Each figure leads and its word follows ("226 ms median"), the way the owner
 * approved it in the demo: the eye scans the numbers along the row and the
 * words only name them.
 *
 * A median worked out from buckets is a median of medians (`viewStats`), and
 * wears "≈" with a title that says so instead of passing for the exact
 * figure a raw window gives. The down time is always there and turns red
 * only when the site was down: "0 min down" is the answer to the question
 * the row exists for. The failure counts appear only when there is one.
 */
import { formatMs, type ViewStats } from "../../../../lib/monitoring";
import { durationText, type T } from "../words";

function Stat({ value, label, title, danger }: { value: string; label: string; title?: string; danger?: boolean }) {
  return (
    <span className="inline-flex items-baseline gap-1.5" title={title}>
      <b className={danger ? "crm-tnum font-semibold text-[var(--color-destructive)]" : "crm-tnum font-semibold text-foreground"}>{value}</b>
      <span>{label}</span>
    </span>
  );
}

export function ViewStatsRow({ stats, t }: { stats: ViewStats; t: T }) {
  const down = stats.downMs > 0 ? durationText(t, stats.downMs / 1000) : `0 ${t("monitoring.unit.min")}`;
  return (
    <div role="group" aria-label={t("monitoring.chart.stats")} className="crm-mut flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[12.5px]">
      <Stat
        value={`${stats.approx ? "≈ " : ""}${formatMs(stats.median)}`}
        label={t("monitoring.chart.stat.median")}
        title={stats.approx ? t("monitoring.chart.approx") : undefined}
      />
      <Stat value={formatMs(stats.slowest)} label={t("monitoring.chart.stat.slowest")} />
      <Stat value={down} label={t("monitoring.chart.stat.down")} danger={stats.downMs > 0} />
      <Stat value={String(stats.checks)} label={t("monitoring.chart.stat.checks", { n: stats.checks })} />
      {stats.down > 0 ? <Stat value={String(stats.down)} label={t("monitoring.chart.stat.failed")} danger /> : null}
      {stats.blind > 0 ? <Stat value={String(stats.blind)} label={t("monitoring.chart.stat.blind", { n: stats.blind })} /> : null}
    </div>
  );
}
